import Vue from 'vue'

import ApiService from '@/core/services/api.service'
import JwtService from '@/core/services/jwt.service'

import { LOGOUT } from './auth.module.js'
import router from '@/router'
import moment from 'moment'

import { eventBus } from '@/main'
import { groupByCountry } from '@/core/utils.js'

// action types
export const GET_USERS = 'getUsers'
export const GET_USER = 'getUser'
export const SWITCH_USER = 'switchUser'
export const CHANGE_USER_EXPIRY = 'changeUserExpiry'
export const CHANGE_USER_DEVICES = 'changeUserDevices'
export const GET_USER_TOKEN = 'getUserToken'
export const REGENERATE_USER_TOKEN = 'regenerateUserToken'
export const RESET_USER_SESSIONS = 'resetUserSessions'
export const GET_USER_SERVERS = 'getUserServers'
export const GET_USER_VPN_SERVERS = 'getUserVpnServers'
export const GET_USER_CONFIG = 'getUserConfig'
export const DOWNLOAD_USER_CONFIG = 'downloadUserConfig'

// mutation types
export const SAVE_USERS = 'saveUsers'
export const SAVE_USER = 'saveUser'
export const SET_USERS_LIMIT = 'setUsersLimit'
export const SET_USERS_PAGE = 'setUsersPage'
export const SET_USERS_GET_PARAM = 'setUsersGetParam'
export const SET_USERS_GET_PARAMS = 'setUsersGetParams'
export const CLEAR_USERS_GET_PARAMS = 'clearUsersGetParams'
export const CLEAR_USERS = 'clearUsers'
export const SELECT_USER = 'selectUser'
export const UNSELECT_USER = 'unselectUser'
export const SAVE_USER_TOKEN = 'saveUserToken'
export const CLEAR_USER_TOKEN = 'clearUserToken'
export const SAVE_USER_SERVERS = 'saveUserServers'
export const CLEAR_USER_SERVERS = 'clearUserServers'
export const SAVE_USER_VPN_SERVERS = 'saveUserVpnServers'
export const CLEAR_USER_VPN_SERVERS = 'clearUserVpnServers'
export const SAVE_USER_CONFIG = 'saveUserConfig'
export const CLEAR_USER_CONFIG = 'clearUserConfig'
export const SAVE_PENDING_SERVER_CONFIG = 'savePendingServerConfig'
export const CLEAR_PENDING_SERVER_CONFIG = 'clearPendingServerConfig'
export const SAVE_GET_USER_CONFIG_ERROR = 'saveGetUserConfigError'
export const CLEAR_GET_USER_CONFIG_ERROR = 'clearGetUserConfigError'

const SET_RESPONSE_STATE = 'setResponseState'
export const TOAST_USERS = 'showToast/UsersList'
export const DEFAULT_USERS_GET_PARAMS = {
  limit: '10',
  page: '1',
  sort: 'created',
  ord: 'desc',
  reseller: null,
  code: null,
  status: null,
  from: null,
  to: null,
  'expire-from': null,
  'expire-to': null
}
export const DEFAULT_USERS_ROUTER_PARAMS = {
  limit: '10',
  page: '1',
  sort: 'created',
  ord: 'desc'
}
export const DEFAULT_USERS_LOCAL_GET_PARAMS = {
  code: null,
  reseller: null,
  status: null,
  page: 1,
  from: null,
  to: null,
  'expire-from': null,
  'expire-to': null
}
export const DEFAULT_RANGE_PARAMS = {
  startDate: null,
  endDate: null
}
export const DEFAULT_SELECTED_USER = {
  _id: undefined,
  item_id: undefined,
  code: '',
  created: '',
  expiry: '',
  deleted: null,
  devices: 0,
  revenuecat_appuserid: null,
  revenuecat_synced: null,
  status: 'off',
  suspended_auto: 'off',
  suspended_by_admin: 'off',
  token: '',
}

// returns user based on _id
function findUser(state, id) {
  return state.usersList.filter(item => item._id === id)[0]
}

// clean each param that is null or undefined
function sanitizeGetRequestPayload(state) {
  const getParams = { ...state.usersGetParams }
  Object.keys(getParams).forEach(key => {
    if (!getParams[key]) {
      delete getParams[key]
    }
  })
  return getParams
}

const state = {
  usersGetParams: { ...DEFAULT_USERS_GET_PARAMS },
  usersList: [],
  totalUsers: 0,
  totalUserPages: 0,
  selectedUser: { ...DEFAULT_SELECTED_USER },
  selectedUserId: null,
  userToken: '',
  userServers: [],
  userVpnServers: [],

  userConfig: '',
  pendingServerConfig: undefined,
  userConfigError: {},

  responses: {
    usersList: false,
    getUser: false,
    switchUser: false,
    changeUserExpiry: false,
    changeUserDevices: false,
    getUserToken: false,
    regenerateUserToken: false,
    resetUserSessions: false,
    getUserServers: false,
    getUserVpnServers: false,
    getUserConfig: false,
    downloadUserConfig: false,
  },
}

const getters = {
  usersList(state) {
    return state.usersList
  },
  usersListLength(state) {
    return state.usersList.length
  },
  usersEmpty(state) {
    return state.usersList.length == 0
  },
  // At first I made getter 'usersGetParams' which returns whole object,
  // but it didn't worked out very well.
  // We need to separate params from state.usersGetParams
  // because Vue sometimes triggers watch() event twice when on of them changes.
  // This happenes because <b-pagination> and <b-form-select> are synched.
  // In that case Axios could make two GET requests at once which is bad.
  usersLimit(state) {
    return state.usersGetParams.limit
  },
  usersPage(state) {
    return state.usersGetParams.page
  },
  usersGetParams(state) {
    return state.usersGetParams
  },
  totalUsers(state) {
    return state.totalUsers
  },
  totalUserPages(state) {
    return state.totalUserPages
  },
  selectedUser(state) {
    return state.selectedUser
  },
  selectedUserId(state) {
    return state.selectedUserId
  },
  selectedUserExist(state) {
    return state.usersList.some(user => user._id == state.selectedUserId)
  },
  userStatusBadge: () => user => {
    const badgeOptions = {
      variant: null,
      icon: null,
      text: null,
    }

    badgeOptions.variant = user.active ? 'success' : 'danger'
    badgeOptions.icon = user.active ? 'check' : 'times'
    badgeOptions.text = user.active ? 'Active' : 'Disabled'

    return badgeOptions
  },
  userCapacity: state => (id, param) => {
    const target = findUser(state, id)
    const statusMap = {
      normal: { status: 'normal', className: 'text-default' },
      warning: { status: 'warning', className: 'text-warning' },
      danger: { status: 'danger', className: 'text-danger' },
    }

    if (target) {
      const percentage = (target[param].total / target[param].limit) * 100

      if (percentage < 70) {
        return statusMap.normal
      } else if (percentage >= 70 && percentage < 90) {
        return statusMap.warning
      } else if (percentage >= 90) {
        return statusMap.danger
      } else {
        return statusMap.normal
      }
    }
  },
  userExpiryStatus: state => id => {
    const target = findUser(state, id)
    if (target) {
      const date = moment(target.expiry)
      const now = moment()
      const diff = date.diff(now, 'hours') // Calculate the difference in hours

      if (diff < 0) {
        return 'expired'
      } else if (diff < 24) {
        return 'tomorrow'
      } else {
        return 'stable'
      }
    }
  },
  userToken(state) {
    return state.userToken
  },
  userTokenEmpty(state) {
    return state.userToken.length == 0
  },
  userServers(state) {
    return state.userServers
  },
  userServersEmpty(state) {
    return state.userServers.length == 0 || Object.keys(state.userServers).length == 0
  },
  userVpnServers(state) {
    return state.userVpnServers
  },
  userVpnServersEmpty(state) {
    return state.userVpnServers.length == 0
  },
  userConfig(state) {
    return state.userConfig
  },
  userConfigEmpty(state) {
    return state.userConfig.length == 0
  },
  pendingServerConfig(state) {
    return state.pendingServerConfig
  },
  isServerConfigPending: state => vpn_code => {
    return state.pendingServerConfig === vpn_code
  },
  userConfigError(state) {
    return state.userConfigError
  },
  userConfigHasError(state) {
    return Object.keys(state.userConfigError).length > 0
  },
  userHasRevenuecatId: state => id => {
    const target = findUser(state, id)
    if (target) {
      return target.revenuecat_appuserid && target.revenuecat_appuserid !== null
    }
  },
  usersResponses: state => type => {
    return state.responses[type]
  }
}

const actions = {
  [GET_USERS](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'usersList', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        const payload = sanitizeGetRequestPayload(context.state)
        ApiService.get(process.env.VUE_APP_USERS, payload)
          .then(({ data }) => {
            context.commit(SAVE_USERS, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_USERS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'usersList', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_USER](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getUser', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_USER}/${id}`)
          .then(({ data }) => {
            context.commit(SAVE_USER, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_USERS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getUser', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [SWITCH_USER](context, { id, memo = undefined }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'switchUser', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        const action = state.selectedUser.status === 'on' ? 'disable' : 'enable'
        const payload = memo ? { memo } : {}
        ApiService.put(`${process.env.VUE_APP_USER}/${id}/${action}`, payload)
          .then(({ data }) => {
            context.dispatch(GET_USER, { id })
            eventBus.$emit(TOAST_USERS, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const error = err.response.data
            eventBus.$emit(TOAST_USERS, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'switchUser', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [CHANGE_USER_EXPIRY](context, { id, expiry }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'changeUserExpire', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.put(`${process.env.VUE_APP_USER}/${id}/expiry`, { expiry })
          .then(({ data }) => {
            context.dispatch(GET_USER, { id })
            eventBus.$emit(TOAST_USERS, {
              message: 'Succesfully updated',
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const error = err.response.data
            eventBus.$emit(TOAST_USERS, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'changeUserExpire', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [CHANGE_USER_DEVICES](context, { id, devices }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'changeUserDevices', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.put(`${process.env.VUE_APP_USER}/${id}/devices`, { devices })
          .then(({ data }) => {
            context.dispatch(GET_USER, { id })
            eventBus.$emit(TOAST_USERS, {
              message: 'Succesfully updated',
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const error = err.response.data
            eventBus.$emit(TOAST_USERS, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'changeUserDevices', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_USER_TOKEN](context, { user_id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getUserToken', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_USER}/${user_id}/token`)
          .then(({ data }) => {
            context.commit(SAVE_USER_TOKEN, data.token)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_USERS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getUserToken', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [REGENERATE_USER_TOKEN](context, { user_id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'regenerateUserToken', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.put(`${process.env.VUE_APP_USER}/${user_id}/token`)
          .then(({ data }) => {
            context.commit(SAVE_USER_TOKEN, data.token)
            eventBus.$emit(TOAST_USERS, {
              message: 'New access token generated',
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_USERS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'regenerateUserToken', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [RESET_USER_SESSIONS](context, { user_id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'resetUserSessions', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.delete(`${process.env.VUE_APP_USER}/${user_id}/vpn/sessions`)
          .then(({ data }) => {
            eventBus.$emit(TOAST_USERS, {
              message: 'Sessions were successfully destroyed',
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_USERS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'resetUserSessions', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_USER_SERVERS](context, { user_id, shouldGroup = false }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getUserServers', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_USER}/${user_id}/servers`)
          .then(({ data }) => {
            context.commit(SAVE_USER_SERVERS, { data, shouldGroup })
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_USERS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getUserServers', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_USER_VPN_SERVERS](context, { user_id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getUserVpnServers', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_USER}/${user_id}/vpn`)
          .then(({ data }) => {
            context.commit(SAVE_USER_VPN_SERVERS, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_USERS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getUserVpnServers', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_USER_CONFIG](context, { id, vpn_code, payload }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(CLEAR_USER_CONFIG)
        context.commit(CLEAR_GET_USER_CONFIG_ERROR)
        context.commit(SAVE_PENDING_SERVER_CONFIG, vpn_code)
        context.commit(SET_RESPONSE_STATE, { type: 'getUserConfig', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_USER}/${id}/vpn/${vpn_code}`, payload)
          .then(({ data }) => {
            context.commit(SAVE_USER_CONFIG, data.servers[0].config_raw)
            resolve(data)
          })
          .catch(err => {
            const error = err.response.data
            eventBus.$emit(TOAST_USERS, {
              message: error.message,
              variant: 'danger'
            })
            context.commit(SAVE_GET_USER_CONFIG_ERROR, error)
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getUserConfig', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [DOWNLOAD_USER_CONFIG](context, { id, vpn_code, payload }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'downloadUserConfig', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_USER}/${id}/vpn/${vpn_code}`, payload, { responseType: 'blob' })
          .then(({ data }) => {
            const blob = new Blob([data], { type: 'application/zip' })
            const link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.download = `config-${vpn_code}-${payload.ip}-${payload.cfg}.zip`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            resolve(data)
          })
          .catch(err => {
            const error = err.response.data
            eventBus.$emit(TOAST_USERS, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'downloadUserConfig', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
}

const mutations = {
  [SAVE_USERS](state, users) {
    state.usersList = users.list
    state.totalUsers = users.total_elements
    state.totalUserPages = users.total_pages
  },
  [CLEAR_USERS](state) {
    state.usersList = []
  },
  [SAVE_USER](state, user) {
    const target = findUser(state, user._id)
    if (target) {
      Object.keys(user).forEach(key => {
        Vue.set(target, key, user[key])
      })
    }
  },
  [SET_USERS_LIMIT](state, limit) {
    state.usersGetParams.limit = limit
  },
  [SET_USERS_PAGE](state, page) {
    state.usersGetParams.page = page
  },
  [SET_USERS_GET_PARAM](state, { value, param }) {
    const origType = typeof state.usersGetParams[param]
    
    if (origType !== 'undefined' && origType !== typeof value) {
      switch (origType) {
        case 'number':
          value = Number(value)
          break
        case 'string':
          value = String(value)
          break
      }
    }

    if (value === 'null') {
      value = null
    }

    state.usersGetParams[param] = value
  },
  [SET_USERS_GET_PARAMS](state, params) {
    const keys = Object.keys(params)
  
    for (const key of keys) {
      const origType = typeof state.usersGetParams[key]
      const newValue = params[key]
  
      if (typeof newValue !== origType) {
        if (state.usersGetParams[key] !== null) {
          switch (origType) {
            case 'number':
              Vue.set(state.usersGetParams, key, Number(newValue))
              break
            case 'string':
              Vue.set(state.usersGetParams, key, String(newValue))
              break
          }
        } else {
          Vue.set(state.usersGetParams, key, newValue)
        }
      } else {
        Vue.set(state.usersGetParams, key, newValue)
      }
    }
  },
  [CLEAR_USERS_GET_PARAMS](state) {
    state.usersGetParams = { ...DEFAULT_USERS_GET_PARAMS }
  },
  [SELECT_USER](state, user) {
    state.selectedUser = user
    state.selectedUserId = user._id
  },
  [UNSELECT_USER](state) {
    state.selectedUser =  { ...DEFAULT_SELECTED_USER }
    state.selectedUserId = undefined
  },
  [SAVE_USER_TOKEN](state, key) {
    state.userToken = key
  },
  [CLEAR_USER_TOKEN](state) {
    state.userToken = ''
  },
  [SAVE_USER_SERVERS](state, { data, shouldGroup }) {
    state.userServers = shouldGroup ? groupByCountry(data) : data
  },
  [CLEAR_USER_SERVERS](state) {
    state.userServers = []
  },
  [SAVE_USER_VPN_SERVERS](state, vpnServers) {
    state.userVpnServers = vpnServers
  },
  [CLEAR_USER_VPN_SERVERS](state) {
    state.userVpnServers = []
  },
  [SAVE_USER_CONFIG](state, config) {
    state.userConfig = config
  },
  [CLEAR_USER_CONFIG](state) {
    state.userConfig = ''
  },
  [SAVE_PENDING_SERVER_CONFIG](state, vpn_code) {
    state.pendingServerConfig = vpn_code
  },
  [CLEAR_PENDING_SERVER_CONFIG](state) {
    state.pendingServerConfig = undefined
  },
  [SAVE_GET_USER_CONFIG_ERROR](state, error) {
    state.userConfigError = error
  },
  [CLEAR_GET_USER_CONFIG_ERROR](state) {
    state.userConfigError = {}
  },
  [SET_RESPONSE_STATE](state, { type, status }) {
    state.responses[type] = status
  },
}

export default {
  state,
  actions,
  mutations,
  getters,
}
